import { Button, Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type PorscheMasterDataModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/PorscheMasterDataModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import PorscheMasterDataModuleActions from './PorscheMasterDataModuleActions';
import PorscheMasterDataModuleDetailsInner from './PorscheMasterDataModuleDetailsInner';

export type PorscheMasterDataModulePageProps = {
    module: PorscheMasterDataModuleWithPermissionsSpecsFragment;
};

const PorscheMasterDataModulePage = ({ module }: PorscheMasterDataModulePageProps) => {
    const { t } = useTranslation(['porscheMasterDataModuleDetails']);
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const navigate = useNavigate();

    const permissions = {
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('porscheMasterDataModuleDetails:descriptions.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('porscheMasterDataModuleDetails:descriptions.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<PorscheMasterDataModuleActions module={module} permissions={permissions} />}
            footer={[
                <Button form="updatePorscheMasterDataModule" htmlType="submit" type="primary">
                    {t('porscheMasterDataModuleDetails:actions.update')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules`)}
            title={
                screens.md
                    ? t('porscheMasterDataModuleDetails:title', { name: module.displayName })
                    : module.displayName
            }
        >
            <PorscheMasterDataModuleDetailsInner module={module} />
        </ConsolePageWithHeader>
    );
};

export default PorscheMasterDataModulePage;
